/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'
import {
	type ChangeEvent,
	type FormEvent,
	Suspense,
	useEffect,
	useState,
} from 'react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useRouter, useSearchParams } from 'next/navigation'
import { usePathname } from 'lib/i18n/routing'
import sendPosthogEvent from '@web/utils/send-posthog-event'
import { FlightIcon } from '@hashicorp/mds-react/components'
import s from './blog-search.module.css'

interface IBlogSearch {
	searchUrl: string
}
export const BlogSearch = (props: IBlogSearch) => {
	return (
		<Suspense
			fallback={
				<form className={s.root}>
					<input type="text" placeholder="Search" disabled />
					<button className={s.button} disabled>
						<FlightIcon
							size={24}
							stretched
							className={s.icon}
							name={'search'}
						/>
					</button>
				</form>
			}
		>
			<BlogSearchInner {...props} />
		</Suspense>
	)
}

const BlogSearchInner = ({ searchUrl }: IBlogSearch) => {
	const formLabel = 'subnavSearch'
	const router = useRouter()
	const pathname = usePathname()

	const searchParams = useSearchParams()
	const searchParamsQuery = searchParams.get('q')
	const [query, setQuery] = useState<string>(searchParamsQuery ?? '')

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setQuery(e?.target?.value)
	}

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault()
		const queryTrimmed = query.trim()

		if (queryTrimmed) {
			sendPosthogEvent('Blog search query', {
				query: queryTrimmed,
			})
		}

		if (pathname === searchUrl) {
			// Set search query param if on search page
			const params = new URLSearchParams(searchParams.toString())
			params.set('q', query)

			router.push(`${searchUrl}?${params.toString()}`)
			return
		}

		if (queryTrimmed) {
			router.push(`${searchUrl}?q=${encodeURIComponent(query)}`)
		}
	}

	useEffect(() => {
		// Clear query if search param is removed
		// eg: If the clear filters button is clicked
		if (searchParamsQuery === null && query !== '') {
			setQuery('')
		}
	}, [searchParamsQuery])

	return (
		<form className={s.root} onSubmit={handleSubmit} role="search">
			<VisuallyHidden>
				<label htmlFor={formLabel} className={s.label}>
					Search
				</label>
			</VisuallyHidden>

			<input
				type="text"
				placeholder="Search"
				id={formLabel}
				name={formLabel}
				className={s.input}
				onChange={handleInputChange}
				value={query}
			/>
			<button className={s.button} aria-label="Search" type="submit">
				<FlightIcon size={24} stretched className={s.icon} name={'search'} />
			</button>
		</form>
	)
}
